/** @file Re-parents a bone (last among its new siblings), keeping its world setup pose by default. */
import { z } from 'zod';
import { docSetupWorlds } from '@pose2d/core/pose/docSetupWorlds';
import { throwError } from '@pose2d/format/throwError';
import type { Doc, Id, LocalTransform } from '@pose2d/format/types';
import { invariant } from '@pose2d/std/invariant';
import { defineCommand } from '../defineCommand';
import { assertNotSocketBone } from '../_shared/assertNotSocketBone';
import { editWithoutIkCycle } from '../ik/editWithoutIkCycle';
import { localFromWorld } from './localFromWorld';
import { nextSiblingIndex } from './nextSiblingIndex';

/** CYCLE at `parent` when `parent` is `target` or below it; params.path spells the loop by bone name. */
function assertNoCycle(doc: Doc, target: Id, parent: Id): void {
  const names: string[] = [];
  for (let id: Id | null = parent; id !== null; id = doc.bones[id]?.parent ?? null) {
    names.unshift(doc.bones[id]?.name ?? id);
    if (id === target) {
      throwError('CYCLE', { path: [...names, names[0] ?? id].join(' -> ') }, { path: 'parent', hint: 'a bone cannot become a child of itself or of one of its descendants' });
    }
  }
}

/** IK_INVALID at `parent` when the bone is the second bone of a two-bone IK and would leave the first one. */
function assertIkChild(doc: Doc, target: Id, parent: Id): void {
  for (const c of Object.values(doc.constraints)) {
    if (c.bones.at(1) === target && c.bones[0] !== parent) {
      throwError('IK_INVALID', { ik: c.name, reason: 'the second bone must be a direct child of the first' }, { path: 'parent', hint: `edit or delete IK ${c.name} first` });
    }
  }
}

/** True when `bone` or one of its ancestors is in `chain`, with `moved` already re-parented under `parent`. */
function belowChainAfterMove(doc: Doc, bone: Id, moved: Id, parent: Id, chain: readonly Id[]): boolean {
  for (let id: Id | null = bone; id !== null; id = id === moved ? parent : (doc.bones[id]?.parent ?? null)) {
    if (chain.includes(id)) return true;
  }
  return false;
}

/** IK_INVALID at `parent` when the move puts an IK target (it or a bone above it) under a bone of that IK's chain. */
function assertIkTargetOutside(doc: Doc, moved: Id, parent: Id): void {
  for (const c of Object.values(doc.constraints)) {
    if (belowChainAfterMove(doc, c.target, moved, parent, c.bones)) {
      throwError('IK_INVALID', { ik: c.name, reason: 'the target must not be a descendant of a constrained bone' }, { path: 'parent', hint: `IK ${c.name} would move its own target; put the bone elsewhere or edit IK ${c.name} first` });
    }
  }
}

/** VALIDATION at `parent` for a generated socket bone: socket.delete removes its subtree with it. */
function assertUserParent(doc: Doc, parent: Id): void {
  if (doc.bones[parent]?.kind !== undefined) {
    throwError('VALIDATION', undefined, { path: 'parent', hint: 'generated socket bone: put the bone under the socket parentBone instead (socket.delete removes everything below it)' });
  }
}

/**
 * Local setup that keeps `id` at its setup world under `parent`. Worlds come from docSetupWorlds (W2-14), so ancestors
 * may use any inherit mode; only the moved bone must inherit normally (VALIDATION at keepWorld otherwise).
 */
function keptSetup(doc: Doc, id: Id, parent: Id): LocalTransform {
  const { name, inherit } = doc.bones[id] ?? { name: id, inherit: 'normal' };
  if (inherit !== 'normal') {
    throwError('VALIDATION', undefined, { path: 'keepWorld', hint: `bone ${name} uses inherit "${inherit}"; keepWorld needs inherit "normal" on the moved bone (set it first, or pass keepWorld: false)` });
  }
  const worlds = docSetupWorlds(doc);
  const parentWorld = worlds.get(parent);
  const world = worlds.get(id);
  invariant(parentWorld && world, 'docSetupWorlds has a world for every bone');
  return localFromWorld(parentWorld, world, 'keepWorld');
}

/**
 * bone.setParent: refuses the root, generated socket bones (moved or as parent), cycles, broken two-bone IK chains and
 * a move that closes an IK dependency loop (CYCLE at `parent` naming the constraints, §6.6).
 */
export const command = defineCommand({
  name: 'bone.setParent',
  description: 'Move a bone under another `parent` (name or id), sorted last among its new siblings. keepWorld (default true) rewrites the local setup so the bone stays where it is, which needs inherit "normal" on the moved bone (its ancestors may use any mode); keepWorld: false keeps the local values. The root and generated socket bones cannot be moved, and a generated socket bone cannot be the new parent.',
  input: z.object({ target: z.string().min(1), parent: z.string().min(1), keepWorld: z.boolean().exactOptional() }).strict(),
  apply(draft, input, ctx) {
    const id = ctx.resolve('bone', input.target);
    const bone = draft.bones[id];
    if (!bone || bone.parent === null) throwError('VALIDATION', undefined, { path: 'target', hint: 'the root bone has no parent; re-parent its children instead' });
    assertNotSocketBone(draft, id);
    const parent = ctx.resolve('bone', input.parent);
    assertNoCycle(draft, id, parent);
    if (bone.parent === parent) return;
    assertUserParent(draft, parent);
    assertIkChild(draft, id, parent);
    assertIkTargetOutside(draft, id, parent);
    editWithoutIkCycle(draft, 'parent', () => {
      if (input.keepWorld !== false) bone.setup = keptSetup(draft, id, parent);
      bone.siblingIndex = nextSiblingIndex(draft, parent);
      bone.parent = parent;
    });
    draft.meta.updatedAt = ctx.now;
  },
});
